<template>
  <div>
    <div class="wrapper">
      <breadcrumb
        :crumbs="[$t('menu.customers'), $t('menu.bargains')]"
      ></breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.productNo"
            :placeholder="$t('placeholder.productNoCustomerProductNo')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.color"
            :placeholder="$t('product.color')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.size"
            :placeholder="$t('product.size')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            v-model="searchForm"
            type="datetime"
            field="createAt"
            :start-placeholder="$t('rules.startAt', [$t('text.createAt')])"
            :end-placeholder="$t('rules.endAt', [$t('text.createAt')])"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            v-model="searchForm"
            type="datetime"
            field="handOverAt"
            :start-placeholder="$t('rules.startAt', [$t('text.handOverAt')])"
            :end-placeholder="$t('rules.endAt', [$t('text.handOverAt')])"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>

    <div class="wrapper table-area">
      <sticky
        :sticky-top="50"
        :viewport="1024"
        :is-rem="false"
        style="min-height: 52px"
      >
        <vxe-toolbar ref="recordsToolbar" custom zoom>
          <template v-slot:buttons></template>
        </vxe-toolbar>
      </sticky>
      <vxe-table
        :data="records"
        @sort-change="sortRecords"
        :sort-config="{ remote: true }"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :span-method="mergeMethod"
        :loading="loading"
        id="bargainsTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        :row-class-name="calculateBgColor"
        size="mini"
        resizable
        round
        highlight-hover-row
        auto-resize
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="flags" :title="$t('text.flag')" width="50">
          <template v-slot="{ row }">
            <span v-if="!row.isBargain">
              <span v-if="row.flags != null && row.flags != ''">
                <flag
                  :name="flag"
                  :size="14"
                  v-for="flag in JSON.parse(row.flags)"
                  :key="flag"
                ></flag>
              </span>
            </span>
            <div v-else>
              <span class="float-left">
                {{ row.serialNo }}
                <small>
                  <currency :code="row.currency"></currency>
                  {{ row.amount }}
                </small>
              </span>
              <span class="float-right">
                {{ $t("menu.customers") }}: {{ row.customerName }}
                <small
                  >{{ $t("text.owner") }}: {{ row.ownerName }}
                  {{ row.shopName }}</small
                >
              </span>
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="images"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.images"></thumbnail>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('text.serialNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <small v-if="row.hurry" class="red"
              >({{ $t("order.hurrySimple") }})</small
            >
            <s v-if="row.workflowState == 'canceled'" class="grey">{{
              row.serialNo
            }}</s>
            <span v-else>{{ row.serialNo }}</span>

            <div>
              <state-label
                :state="row.workflowState"
                :permit="Permit.canActions('orderDealView')"
              >
                <span v-if="Permit.canActions('orderDealView')">{{
                  $t("orderStates." + row.workflowState)
                }}</span>
                <span v-else>{{
                  $t("orderStatesNoPermit." + row.workflowState)
                }}</span>
              </state-label>
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="handOverAt"
          :title="$t('text.handOverAt')"
          min-width="130"
          sortable
        >
          <template v-slot="{ row }">
            {{ row.handOverAt }}
            <div v-if="row.confirmAt != null" class="font10 green">
              {{ $t("order.confirm") }}:{{ row.confirmAt }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="productNo"
          :title="$t('product.productNo')"
          min-width="100"
          sortable
        >
          <template v-slot="{ row }">
            {{ row.productNo }}
            <small
              v-if="
                row.customerProductNo != null && row.customerProductNo != ''
              "
              >({{ row.customerProductNo }})</small
            >

            <div class="font10">
              {{ row.categoryName }}
              <span v-if="row.productBrand != null">/</span>
              {{ row.productBrand }}
            </div>
            <div>{{ row.color }} {{ row.size }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          v-if="Permit.canActions('globalSalePrice')"
          field="price"
          :title="$t('order.price')"
          min-width="60"
        >
          <template v-slot="{ row }">
            <currency :code="row.currency"></currency>
            {{ row.price }}
            <small v-if="row.discountRate != null && row.discountRate != 0"
              >({{ row.discountRate }}%)</small
            >
            <div
              v-if="row.extraCharge != null && row.extraCharge != 0"
              class="font10"
            >
              {{ $t("order.extraChargeSimple") }}:
              <currency :code="row.currency"></currency>
              {{ row.extraCharge }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="quantity"
          :title="$t('text.quantity')"
          min-width="60"
        >
          <template v-slot="{ row }">
            {{ row.quantity }}{{ row.unit || "" }}
            <div class="font10">
              <el-popover placement="right" width="440" trigger="click">
                <vxe-table
                  :data="JSON.parse(row.deliverInfos || '[]')"
                  id="deliverInfosTable"
                  row-id="id"
                  ref="deliverInfosTable"
                  class="recordsTable"
                  size="mini"
                  resizable
                  round
                  auto-resize
                  highlight-hover-row
                  :scroll-y="{ enabled: false }"
                >
                  <vxe-table-column
                    field="发货方式"
                    :title="$t('delivery.deliveryMethode')"
                  />
                  <vxe-table-column
                    field="快递公司"
                    :title="$t('delivery.express')"
                  />
                  <vxe-table-column
                    field="渠道"
                    :title="$t('delivery.channel')"
                  />
                  <vxe-table-column
                    field="跟踪号"
                    :title="$t('delivery.tracking')"
                  />
                </vxe-table>
                <span slot="reference" class="popover-reference"
                  >{{ $t("order.delivered") }}:{{ row.deliveredQuantity
                  }}{{ row.unit || "" }}</span
                >
              </el-popover>
            </div>
            <div v-if="row.customerReceiveAt != null" class="font10">
              {{ $t("order.customerReceiveAt") }}:{{ row.customerReceiveAt }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="dealType"
          :title="$t('order.dealType')"
          min-width="70"
        >
          <template v-slot="{ row }">
            <span v-if="row.dealType !== null">{{
              $t("orderDealTypes." + row.dealType)
            }}</span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="createAt"
          :title="$t('text.createAt')"
          min-width="100"
        />

        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="60"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              :content="row.remark"
              :number="10"
              type="richText"
            />
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips"></div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { table, page } from "@/utils/mixins.js";
import PagerConfig from "@/config/pager.js";
import Currency from "@/components/Currency";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import DateRange from "@/components/DateRange";

import Bargain from "@/api/bargain.js";
import Order from "@/api/order.js";
import utils from "@/utils/index.js";
import { flagsWithClear, flagsWithNone } from "@/utils/enums.js";

export default {
  mixins: [
    page,
    table.pager,
    table.narrow,
    table.search,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: { Currency, RawHtmlLimitNumber, DateRange },
  data() {
    return {
      narrows: [
        [
          {
            label: this.$t("order.nearRange"),
            items: Order.nearRange,
          },
          {
            label: this.$t("order.passRange"),
            items: Order.passRange,
          },
          {
            label: this.$t("text.flag"),
            items: flagsWithNone.map((flag) => {
              return { flag: flag, key: flag, condition: { flag: flag } };
            }),
          },
        ],
      ],
      searchForm: {
        keyword: null,
        productNo: null,
        createAtStartAt: null,
        createAtEndAt: null,
        handOverAtStartAt: null,
        handOverAtEndAt: null,
        ownerId: null,
        shopId: null,
        categoryId: null,
        productBrand: null,
        color: null,
        size: null,
      },
      showBargain: false,
      showOrder: false,
      currentRecordId: null,
    };
  },
  computed: {
    implicit() {
      return {
        customerId: this.$route.params.id,
      };
    },
  },
  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.narrowsWorkflowStates();
  },
  methods: {
    narrowsWorkflowStates() {
      const self = this;
      if (self.Permit.canActions("orderDealView")) {
        var states = Order.states_pre;
        if (self.Permit.canActionsConfigDealType("available")) {
          states = states.concat(Order.states_available);
        }
        if (self.Permit.canActionsConfigDealType("manufacture")) {
          states = states.concat(Order.states_manufacture);
        }
        if (self.Permit.canActionsConfigDealType("purchase")) {
          states = states.concat(Order.states_purchase);
        }
        self.narrows.push([
          {
            label: self.$t("text.workflowState"),
            items: states.concat(Order.states_after).map((s) => {
              return {
                key: s,
                label: self.$t("orderStates." + s),
                condition: { workflowStates: [s] },
              };
            }),
          },
        ]);
      } else {
        self.narrows.push([
          {
            label: self.$t("text.workflowState"),
            items: Order.states_no_permint.map((s) => {
              return {
                key: s.key,
                label: self.$t("orderStates." + s.key),
                condition: { workflowStates: s.workflowStates },
              };
            }),
          },
        ]);
      }
    },
    //
    dataProvider(params) {
      return Bargain.search(params);
    },
    recordsDecorate(records) {
      this.records = this.buildTableData(records);
      this.mergeCells = this.buildMergeCells(records);
    },
    buildTableData(bargains) {
      let rs = [];
      bargains.forEach((bg) => {
        let bOrder = {
          isBargain: true,
          id: bg.id,
          uuid: bg.uuid,
          serialNo: bg.serialNo,
          shopId: bg.shopId,
          shopName: bg.shopName,
          settleType: bg.settleType,
          amount: bg.amount,
          currency: bg.currency,
          customerName: bg.customerName,
          customerCountry: bg.customerCountry,
          customerProvince: bg.customerProvince,
          customerCity: bg.customerCity,
          customerAddress: bg.customerAddress,
          customerPhone: bg.customerPhone,
          customerZipcode: bg.customerZipcode,
          customerType: bg.customerType,
          customerSource: bg.customerSource,
          customerEmail: bg.customerEmail,
          deliveryRemark: bg.deliveryRemark,
          groupJson: bg.groupJson,
          remark: bg.remark,
          ownerId: bg.ownerId,
          ownerName: bg.ownerName,
          createAt: bg.createAt,
          plateform: bg.plateform,
          plateformBargainId: bg.plateformBargainId,
          cnyAmount: bg.cnyAmount,
          replaceCustomerId: bg.replaceCustomerId,
          lastDealtAt: bg.lastDealtAt,
          customer: bg.customer,
          productAmount: bg.productAmount,
          // currentMonthAmount: bg.currentMonthAmount,
          plateformStoreId: bg.plateformStoreId,
          financeDeliveryAt: bg.financeDeliveryAt,
          deliveryAudit: bg.deliveryAudit,
          financeAudit: bg.financeAudit,
          payAmount: bg.payAmount,
          productQty: bg.productQty,
          differAmount: bg.differAmount,
          ignoreAmount: bg.ignoreAmount,
        };
        rs.push(bOrder); //将bargain的数据转换到格式，放到数据列表
        bg.orders.forEach((order) => {
          rs.push(order);
        });
      });
      return rs;
    },
    buildMergeCells(bargains) {
      let mcs = [];
      for (let i = 0, rowIdx = 0; i < bargains.length; i++) {
        // mcs.push({ row: rowIdx, col: 0, rowspan: 1, colspan: 10 });
        if (this.Permit.canActions("globalSalePrice")) {
          mcs.push({ row: rowIdx, col: 0, rowspan: 1, colspan: 10 });
        } else {
          mcs.push({ row: rowIdx, col: 0, rowspan: 1, colspan: 9 });
        }
        rowIdx = rowIdx + bargains[i].orders.length + 1;
      }
      return mcs;
    },
    calculateBgColor({ row, rowIndex }) {
      return row.isBargain
        ? "light-blue-bg"
        : this.rowBgColor({ row: row, rowIndex: rowIndex });
    },
    isNear(record) {
      //定义近交期
      return false;
    },
    isPass(record) {
      //定义过交期
      return false;
    },
  },
};
</script>
<style scoped>
</style>